"use client";

import cx from "classnames";
import Image from "next/image";
import {
  HEAD_TO_HEAD_STATUS_MAP,
  HEAD_TO_HEAD_TEAM_LINK_CLICKED,
  SPORTSBOOK_URL,
} from "@/constants/amplitude";
import { getFormattedMonthDay } from "@/utils/date";
import { useAmplitude } from "@/utils/hooks/useAmplitude";
import { useHeadToHeadContext } from "@/utils/hooks/useHeadToHeadContext";
import { filterEmpty } from "@/utils/index";
import TimeStampClient from "@/utils/TimeStampClient";
import { TeamGameLog } from "./types";
import styles from "./OverviewGameLog.module.scss";

interface TeamLogProps {
  gameLog: TeamGameLog[];
}

export default function TeamLog({ gameLog }: TeamLogProps) {
  const { eventName, gameState } = useHeadToHeadContext();
  const { logAmplitudeEvent } = useAmplitude();
  const ampProps = {
    "Event Name": eventName,
    Module: "game_log",
    "Page State": HEAD_TO_HEAD_STATUS_MAP[gameState],
  };

  return (
    <div className={styles.listContainer}>
      <div className={styles.listHeader}>
        <span>Date</span>
        <span>Opponent</span>
        <span>Result</span>
        <span>Spread</span>
      </div>
      <div>
        {filterEmpty(gameLog ?? []).map((log) => {
          const dateStr: string = log.timestamp
            ? TimeStampClient({
                isoString: new Date(log.timestamp * 1000).toISOString(),
                formatter: getFormattedMonthDay,
              })
            : "";
          const opponentName = log.opponentTeam?.abbreviation || "";
          const opponentIcon = log.opponentTeam?.iconUrl || "";
          const vsOrAt = log.homeTeam ? "vs" : "@";

          let resultLetter: "W" | "L" | "" = "";
          if (log.result === "WIN") resultLetter = "W";
          else if (log.result === "LOSS") resultLetter = "L";

          const scores = log.score ? log.score.split("-").map(Number) : [];
          let teamScore = 0,
            oppScore = 0;
          if (scores.length === 2) {
            if (resultLetter === "W") {
              teamScore = Math.max(scores[0], scores[1]);
              oppScore = Math.min(scores[0], scores[1]);
            } else if (resultLetter === "L") {
              teamScore = Math.min(scores[0], scores[1]);
              oppScore = Math.max(scores[0], scores[1]);
            } else {
              teamScore = scores[0];
              oppScore = scores[1];
            }
          }
          const margin = teamScore - oppScore;
          const spreadVal = parseFloat(String(log.spread || "0"));
          const covered =
            spreadVal < 0
              ? margin >= Math.abs(spreadVal)
              : margin > 0 || Math.abs(margin) < spreadVal;
          const spreadDisplay =
            spreadVal > 0 ? `+${spreadVal}` : spreadVal.toString();
          const spreadClass = covered ? styles.win : styles.loss;
          const scoreDisplay = log.score ? ` ${log.score}` : "";

          const handleClick = () => {
            logAmplitudeEvent(HEAD_TO_HEAD_TEAM_LINK_CLICKED, {
              ...ampProps,
              "Link Text": opponentName,
              "Link URL": log.opponentTeam?.teamSportsbookUrl ?? SPORTSBOOK_URL,
              "Team Name": log.opponentTeam?.name ?? opponentName,
            });
          };

          return (
            <div
              className={styles.gameLogDetails}
              key={log.gameId ?? log.timestamp}
            >
              <span>{dateStr}</span>
              <span className={styles.opponentLine}>
                <span className={styles.opponentWrapper}>
                  <span>{vsOrAt}</span>
                  <span className={styles.opponentContent}>
                    <Image
                      width={21}
                      height={21}
                      src={opponentIcon}
                      alt={opponentName}
                    />
                    <a
                      href={
                        log.opponentTeam?.teamSportsbookUrl ?? SPORTSBOOK_URL
                      }
                      onClick={handleClick}
                      className={styles.opponentName}
                    >
                      {opponentName}
                    </a>
                  </span>
                </span>
              </span>
              <span>
                <span
                  className={cx(styles.resultLetter, {
                    [styles.win]: resultLetter === "W",
                    [styles.loss]: resultLetter === "L",
                  })}
                >
                  {resultLetter}{" "}
                </span>
                {scoreDisplay}
              </span>
              <span className={spreadClass}>{spreadDisplay}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
